import { getCategoryById } from '../data/categories';
import { getShoeSizes } from '../data/shoeSizes';
import { getClothingSizes } from '../data/clothingSizes';
import { getJeansSizes } from '../data/jeansSizes';
import { getKidsShoeSizes } from '../data/kidsShoeSizes';

const normalize = (value) => String(value).trim().toLowerCase();

function matchValue(rowValue, input) {
  const row = normalize(rowValue);
  const val = normalize(input);
  if (row === val) return true;
  if (row.includes('-')) {
    const [min, max] = row.split('-').map((s) => s.trim());
    const num = parseFloat(val);
    if (!Number.isNaN(num)) {
      const minN = parseFloat(min);
      const maxN = parseFloat(max);
      return num >= minN && num <= maxN;
    }
    return row === val;
  }
  return row === val;
}

export function getDataset(categoryId, gender) {
  switch (categoryId) {
    case 'shoes':
      return getShoeSizes(gender);
    case 'mens-clothing':
      return getClothingSizes('men');
    case 'womens-clothing':
      return getClothingSizes('women');
    case 'jeans':
      return getJeansSizes(gender);
    case 'kids-shoes':
      return getKidsShoeSizes();
    default:
      return [];
  }
}

export function getSystemsForCategory(categoryId) {
  switch (categoryId) {
    case 'shoes':
    case 'kids-shoes':
      return [
        { id: 'eu', labelKey: 'eu' },
        { id: 'uk', labelKey: 'uk' },
        { id: 'us', labelKey: 'us' },
        { id: 'cm', labelKey: 'cm' },
      ];
    case 'jeans':
      return [
        { id: 'waistIn', labelKey: 'waistIn' },
        { id: 'eu', labelKey: 'eu' },
        { id: 'uk', labelKey: 'uk' },
        { id: 'us', labelKey: 'us' },
        { id: 'international', labelKey: 'international' },
        { id: 'waistCm', labelKey: 'waistCm' },
      ];
    default:
      return [
        { id: 'international', labelKey: 'international' },
        { id: 'eu', labelKey: 'eu' },
        { id: 'uk', labelKey: 'uk' },
        { id: 'us', labelKey: 'us' },
        { id: 'chestCm', labelKey: 'chestCm' },
        { id: 'waistCm', labelKey: 'waistCm' },
      ];
  }
}

export function getSizeOptions(categoryId, gender, systemId) {
  const data = getDataset(categoryId, gender);
  const seen = new Set();
  const options = [];

  data.forEach((row) => {
    const value = row[systemId];
    if (value && !seen.has(value)) {
      seen.add(value);
      options.push({ value, label: value });
    }
  });

  return options;
}

export function convertSize({ categoryId, gender, systemId, sizeValue }) {
  if (!categoryId || !systemId || sizeValue === '' || sizeValue == null) {
    return null;
  }

  const category = getCategoryById(categoryId);
  const resolvedGender = gender || category?.defaultGender;
  const data = getDataset(categoryId, resolvedGender);

  const match = data.find((row) => matchValue(row[systemId], sizeValue));
  if (!match) return null;

  return {
    row: match,
    categoryId,
    gender: resolvedGender,
    inputSystem: systemId,
    inputValue: sizeValue,
  };
}

export function resolveGender(categoryId, selectedGender) {
  const category = getCategoryById(categoryId);
  if (category?.defaultGender) return category.defaultGender;
  return selectedGender || 'men';
}

function parseRangeMidpoint(rangeStr) {
  const str = String(rangeStr);
  if (str.includes('-')) {
    const [a, b] = str.split('-').map((s) => parseFloat(s.trim()));
    if (!Number.isNaN(a) && !Number.isNaN(b)) return (a + b) / 2;
  }
  const n = parseFloat(str);
  return Number.isNaN(n) ? null : n;
}

function rowMatchesMeasurement(row, field, cmValue) {
  const mid = parseRangeMidpoint(row[field]);
  if (mid == null) return null;
  return { row, diff: Math.abs(mid - cmValue) };
}

export function findSizeByMeasurement({ categoryId, gender, measurementType, cmValue }) {
  const val = parseFloat(cmValue);
  if (Number.isNaN(val)) return null;

  const resolvedGender = resolveGender(categoryId, gender);
  const data = getDataset(categoryId, resolvedGender);

  let field = 'cm';
  if (categoryId === 'shoes') field = 'cm';
  else if (categoryId === 'jeans') field = 'waistCm';
  else field = measurementType === 'waist' ? 'waistCm' : 'chestCm';

  let best = null;
  data.forEach((row) => {
    const candidate = rowMatchesMeasurement(row, field, val);
    if (!candidate) return;
    if (!best || candidate.diff < best.diff) best = candidate;
  });

  if (!best) return null;

  return {
    row: best.row,
    categoryId,
    gender: resolvedGender,
    inputSystem: field,
    inputValue: String(cmValue),
    matchedByMeasurement: true,
    measurementDiff: best.diff,
  };
}

export function getAdjacentSizes(categoryId, gender, row) {
  const resolvedGender = resolveGender(categoryId, gender);
  const data = getDataset(categoryId, resolvedGender);
  const idx = data.findIndex(
    (r) =>
      r === row ||
      (r.eu === row.eu && r.international === row.international && r.waistIn === row.waistIn)
  );
  if (idx === -1) return { smaller: null, larger: null, index: -1 };
  return {
    smaller: idx > 0 ? data[idx - 1] : null,
    larger: idx < data.length - 1 ? data[idx + 1] : null,
    index: idx,
  };
}

export function formatResultSummary(result, labels, categoryId) {
  if (!result?.row) return '';
  const { row } = result;
  const parts = [];
  if (categoryId === 'shoes' || categoryId === 'kids-shoes') {
    if (row.eu) parts.push(`EU ${row.eu}`);
    if (row.age) parts.push(`Age ${row.age}`);
    if (row.uk) parts.push(`UK ${row.uk}`);
    if (row.us) parts.push(`US ${row.us}`);
    if (row.cm) parts.push(`${row.cm} cm`);
  } else if (categoryId === 'jeans') {
    if (row.waistIn) parts.push(`Waist ${row.waistIn}"`);
    if (row.eu) parts.push(`EU ${row.eu}`);
    if (row.international) parts.push(row.international);
  } else {
    if (row.international) parts.push(row.international);
    if (row.eu) parts.push(`EU ${row.eu}`);
    if (row.chestCm) parts.push(`${labels.chestCm || 'Chest'} ${row.chestCm} cm`);
  }
  return parts.join(' · ');
}
